"use client";
import { usePathname } from 'next/navigation';
import { PreceptorSideNav } from '@/modules/preceptors/components/preceptor-sidenav';

// Canonical preceptor layout (singular). Sidebar only appears for protected pages.
// Protected segment is implemented using the (protected) route group. In the URL the
// group name is omitted, so we infer protected pages by excluding known public paths.
const PUBLIC_PATH_PREFIXES = [
  '/preceptor',
  '/preceptor/search',
  '/preceptor/np/search',
  '/preceptor/img/search',
  '/preceptor/pa/search',
  '/preceptor/login',
  '/preceptor/signup',
  '/preceptor/track',
  '/preceptor/ap/login',
  '/preceptor/ap/signup',
  '/preceptor/np/login',
  '/preceptor/np/signup',
  '/preceptor/pa/login',
  '/preceptor/pa/signup',
  '/preceptor/onboarding',
  '/preceptor/verification',
];

function isPublicPath(pathname: string): boolean {
  if (pathname === '/preceptor') {
    return true;
  }

  return PUBLIC_PATH_PREFIXES.some(
    (prefix) => prefix !== '/preceptor' && (pathname === prefix || pathname.startsWith(`${prefix}/`))
  );
}

export default function PreceptorLayout({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const showSidebar = !isPublicPath(pathname);
  // Rotation search pages use a plain white background (others keep the app gray).
  const isSearchPage = pathname.includes('/search');

  if (!showSidebar) {
    return (
      <div className={`min-h-screen ${isSearchPage ? 'bg-white' : 'bg-[var(--app-bg,#f9fafb)]'}`}>
        {/* Search pages are full-bleed (no padding) on all sizes — desktop renders a
            full-width header, mobile a full-width filter bar. */}
        <main className={isSearchPage ? 'p-0' : 'px-3 py-6'}>{children}</main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--app-bg,#f9fafb)]">
      <PreceptorSideNav />
      <main className="ml-56 px-3 pt-6">{children}</main>
    </div>
  );
}
